import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDropzone } from "react-dropzone";
import _ from "lodash";
import CloudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import LoopIcon from "@mui/icons-material/LoopOutlined";
import { getAuthData } from "../../../../../services/storage/LocalStorage";
import { showSnackbarV2 } from "../../../../../state/actions/notification";
import config from "../../../../../utils/config/config";
import { BotApiServices } from "../../../../../services/api/BotApiServices";
import { ButtonContainer, ButtonProgress, Container } from "./Styled";


const R = require("ramda");
const maxSize = 3; //in MB
const conversionFactor = 1024000; // 1MB to bytes
const maxSizeInBytes = maxSize * conversionFactor; //as dropzone needs values in bytes


function fileSizeValidator(file) {
	if (file && file.size > maxSizeInBytes) {
		return {
			code: `size-${maxSize}-too-large`,
			message: `Size too large. Upload a file less than ${maxSize} MB in size`,
		};
	}
	return null;
};

const ImageUploadField = ({
	handleClick,
	id,
	title,
	type,
	value,
	fileName,
	fileScope,
	readOnly,
}) => {

	const sectionRef = useRef(null);
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const [showUploadBtn, setShowUploadBtn] = useState(true);
	const [files, setFiles] = useState([]);

	const selectedConversation = useSelector((state) => state.chats.selectedConversation);
	const user = useSelector((state) => state.user);

	const selectedConversationId = selectedConversation?.conversationId;
	const currentBotId = selectedConversation?.bot?.botId;
	const currentUserDomain = selectedConversation?.userDomain;

	const [scopeId, setScopeId] = useState(() => {
		switch (fileScope) {
			case "bot": {
				return currentBotId;
			}

			case "domain": {
				return currentUserDomain;
			}

			case "conversation":
			default: {
				return selectedConversationId;
			}
		}
	});

	const { user: { userTimezone } } = getAuthData();
	const defaultUserTimezone = "Etc/UTC";

	const {
		acceptedFiles,
		fileRejections,
		getRootProps,
		getInputProps,
		inputRef,
		rootRef,
	} = useDropzone({
		multiple: false,
		disabled: isLoading || readOnly,
		accept: "image/*",
		onDropAccepted: (acceptedFiles) => {
			setFiles(
				acceptedFiles.map((file) =>
					Object.assign(file, {
						preview: URL.createObjectURL(file),
					})
				)
			);
			setShowUploadBtn(false);
			handleClick &&
				handleClick(inputRef.current, type, id, title, scopeId, fileScope);
		},
		validator: fileSizeValidator,
	});

	useEffect(() => () => {
		// Make sure to revoke the data uris to avoid memory leaks
		files.forEach((file) => URL.revokeObjectURL(file.preview));
	}, [files]);

	useEffect(() => {
		if (value) {
			downloadFile(value, fileName);
		}
	}, [value]);


	const downloadFile = async (value, fileName) => {
		setIsLoading(true);
		if (user.isOnline) {
			const url = `${config.filesAPI}/downloadwithsignedurl/${fileScope || "conversation"}/${scopeId}/${value}`;
			console.log("Download file url:", url);

			try {
				const response = await BotApiServices.downloadSignedUrlFile(url);
				if (response?.status === 200 && response?.data?.signedUrl) {
					const signedUrl = response.data.signedUrl;
					try {
						const fileResponse = await BotApiServices.downloadFile(signedUrl);

						if (fileResponse?.status === 200 && fileResponse?.data instanceof Blob) {
							const blob = fileResponse.data;
							const downloadUrl = window.URL.createObjectURL(blob);
							if (sectionRef && sectionRef.current !== null) {
								sectionRef.current.style.backgroundImage = `url(${downloadUrl})`;
							}
							setShowUploadBtn(false);
							setIsLoading(false);
						} else {
							throw new Error("Invalid blob response");
						}
					} catch (downloadError) {
						console.error("Download file error:", downloadError);
						dispatch(showSnackbarV2("error", "Failed to download file"));
					}

				} else {
					console.error("Signed URL not received:", response);
					dispatch(showSnackbarV2("error", "Failed to generate signed URL"));
				}

				setIsLoading(false);
			} catch (signedUrlError) {
				console.error("Error generating signed URL:", signedUrlError);
				dispatch(showSnackbarV2("error", "Failed to generate signed URL"));
				setIsLoading(false);
			}
		} else {
			dispatch(
				showSnackbarV2(
					"error",
					"You will be able to download this file once you are back online"
				)
			);
			setIsLoading(false);
		}
	};

	const fileRejectionItems = fileRejections.map(({ file, errors }, index) => (
		<ul
			style={{
				color: "red",
				textAlign: "center",
				listStylePosition: "inside",
				listStyleType: "none",
				margin: 0,
				padding: 0,
			}}
			key={index}
		>
			{errors.map((e) => (
				<li key={e.code}>{e.message}</li>
			))}
		</ul>
	));

	return (
		<>
			<Container
				ref={sectionRef}
				style={{
					backgroundImage: `url(${files[0]?.preview})`,
					backgroundRepeat: "no-repeat",
					backgroundSize: "contain",
					backgroundPosition: "center center",
					borderRadius: "20px",
					height: "400px",
					border: readOnly ? "solid 1.2px #e8e8e8 !important" : "",
				}}
			>
				<div
					{...getRootProps({ className: "dropzone" })}
					style={{
						width: "inherit",
						height: "inherit",
						position: "relative",
					}}
				>
					<input id={id} name={id} {...getInputProps({})} accept="image/*" />
					<ButtonContainer
						readOnly={readOnly}
						sx={
							showUploadBtn
								? {
									position: "absolute",
									top: "50%",
									left: "50%",
									transform: "translate(-50%, -50%)",
									color: "black",
								}
								: {
									position: "absolute",
									left: "10px",
									top: "0px",
									color: "black",
								}
						}
						variant="outlined"
						disabled={isLoading || readOnly}
						startIcon={
							isLoading ? (
								<ButtonProgress size={24} />
							) : (
								(showUploadBtn && <CloudUploadIcon />) || <LoopIcon />
							)
						}
					>
						{showUploadBtn ? "Upload Photo" : "Change Photo"}
					</ButtonContainer>
				</div>
			</Container>
			{fileRejectionItems}
			<div style={{ color: " #9c9ea7", textAlign: "center" }}>
				The image size must not exceed 3MB. Upload an image close to a 4:3 aspect ratio for optimal viewing.
			</div>
		</>
	);
};

export default ImageUploadField;
